import type { Page } from "../types.js";

// URL <-> page mapping for the app's hand-rolled router.
//
// The app has no router library (see CLAUDE.md): App owns a `page` string-union
// plus the novel id some pages need (detail/player/editor). These two pure
// functions translate that pair to and from the address bar, so a refresh or a
// shared link lands on the same screen instead of dropping back to the landing
// page. `parseRoute(buildPath(p, id))` round-trips for every page, which is what
// keeps App's state->URL sync effect from looping.

export interface RouteState {
  page: Page;
  /** Novel id carried by detail/player/editor routes; null for the rest. */
  novelId: string | null;
}

// Param-less pages, keyed by their canonical first path segment.
const STATIC_PATHS: Record<string, Page> = {
  "": "landing",
  catalog: "catalog",
  create: "create",
  status: "status",
  profile: "profile",
  admin: "admin",
  signin: "auth",
  terms: "terms",
  privacy: "privacy",
  refund: "refund",
  pricing: "pricing",
  // The pre-launch "About this beta" page lived at /legal; old links land on the Terms.
  legal: "terms",
  showcase: "showcase",
  genloop: "genloop"
};

const PAGE_TO_STATIC: Partial<Record<Page, string>> = {
  landing: "/",
  catalog: "/catalog",
  create: "/create",
  status: "/status",
  profile: "/profile",
  admin: "/admin",
  auth: "/signin",
  terms: "/terms",
  privacy: "/privacy",
  refund: "/refund",
  pricing: "/pricing",
  showcase: "/showcase",
  genloop: "/genloop"
};

// Id-bearing pages: /<segment>/<id>. `/u/<username>` carries an author key (the
// username, or an owner id) in the same `novelId` slot (the router's single id
// holder); the rest carry a novel id or a novel's link key (lib/novelLinks.ts).
const ID_SEGMENT_TO_PAGE: Record<string, Page> = {
  novel: "detail",
  // The short share link, /n/<code>: the novel page (the URL sync then writes
  // its full /novel/<slug>-<code> address).
  n: "detail",
  play: "player",
  edit: "editor",
  u: "author"
};
const PAGE_TO_ID_SEGMENT: Partial<Record<Page, string>> = {
  detail: "novel",
  player: "play",
  editor: "edit",
  author: "u"
};

// The dev-only debug surfaces (/showcase, /genloop) are never addressable in a
// production build — a visitor typing them there just gets the landing page.
const DEV_ONLY_PAGES: ReadonlySet<Page> = new Set(["showcase", "genloop"]);
function devPageAllowed(page: Page): boolean {
  return !DEV_ONLY_PAGES.has(page) || Boolean(import.meta.env.DEV);
}

/**
 * Parse a pathname into a page + optional novel id. Unknown paths (and the
 * dev-only /showcase in a production build) fall back to the landing page; an
 * id-bearing route missing its id falls back to a sensible home rather than
 * rendering a page with a null id.
 */
export function parseRoute(pathname: string): RouteState {
  const segments = pathname.split("/").filter(Boolean).map(decodeSegment);
  if (segments.length === 0) return { page: "landing", novelId: null };

  const [head, second] = segments;

  const idPage = ID_SEGMENT_TO_PAGE[head];
  if (idPage) {
    if (!second) {
      // /edit with no id -> My Novels; /novel or /play with no id -> catalog.
      return { page: idPage === "editor" ? "profile" : "catalog", novelId: null };
    }
    return { page: idPage, novelId: second };
  }

  if (segments.length === 1) {
    const page = STATIC_PATHS[head];
    if (page && devPageAllowed(page)) {
      return { page, novelId: null };
    }
  }

  return { page: "landing", novelId: null };
}

/** Build the canonical path for a page + its novel id (inverse of parseRoute). */
export function buildPath(page: Page, novelId: string | null): string {
  const idSegment = PAGE_TO_ID_SEGMENT[page];
  if (idSegment) {
    // An id-bearing page with no id has no canonical URL — fall back to its home.
    if (!novelId) return page === "editor" ? "/profile" : "/catalog";
    return `/${idSegment}/${encodeURIComponent(novelId)}`;
  }
  if (!devPageAllowed(page)) return "/";
  return PAGE_TO_STATIC[page] ?? "/";
}

// A malformed %-escape in the URL must not throw; fall back to the raw segment.
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
